import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Crown, Gem, Sparkles, CheckCircle, Download, Share2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import { metaBuildsApi, entriesApi, userProgressApi, PLACEHOLDER_IMAGE } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useSiteLock } from '../context/SiteLockContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const MetaPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { language, t } = useLanguage();
    const { isPageLocked } = useSiteLock();
    const [builds, setBuilds] = useState([]);
    const [relics, setRelics] = useState([]);
    const [accessories, setAccessories] = useState([]);
    const [userProgress, setUserProgress] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [exporting, setExporting] = useState(null);
    const buildRefs = useRef({});

    useEffect(() => {
        loadData();
    }, [user]);

    const loadData = async () => {
        try {
            const [buildsRes, relicsRes, accessoriesRes] = await Promise.all([
                metaBuildsApi.getAll(),
                entriesApi.getAll('relics'),
                entriesApi.getAll('accessories')
            ]);
            setBuilds(buildsRes.data || []);
            const relicsData = relicsRes.data;
            const accessoriesData = accessoriesRes.data;
            setRelics(Array.isArray(relicsData) ? relicsData : (relicsData?.items || []));
            setAccessories(Array.isArray(accessoriesData) ? accessoriesData : (accessoriesData?.items || []));

            // Lade User Progress für den aktuellen Benutzer
            if (user?.discord_id) {
                const progressRes = await userProgressApi.getAll(user.discord_id);
                const progressMap = {};
                (progressRes.data || []).forEach(p => {
                    progressMap[p.item_id] = p;
                });
                setUserProgress(progressMap);
            }
        } catch (error) {
            console.error('Failed to load meta data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getRelicById = (id) => relics.find(r => r.id === id);
    const getAccessoryById = (id) => accessories.find(a => a.id === id);
    const isReceived = (itemId) => userProgress[itemId]?.received || false;

    const exportBuildAsImage = async (buildId, buildTitle) => {
        const element = buildRefs.current[buildId];
        if (!element) return;

        setExporting(buildId);
        try {
            const canvas = await html2canvas(element, {
                backgroundColor: '#0A0A0A',
                scale: 2,
                useCORS: true,
                allowTaint: true,
                logging: false,
            });
            
            // Download als PNG
            const link = document.createElement('a');
            link.download = `meta-build-${buildTitle.replace(/\s+/g, '-').toLowerCase()}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
            
            toast.success(language === 'de' ? 'Build als Bild exportiert!' : 'Build exported as image!');
        } catch (error) {
            console.error('Export failed:', error);
            toast.error(language === 'de' ? 'Export fehlgeschlagen' : 'Export failed');
        } finally {
            setExporting(null);
        }
    };

    const copyBuildToClipboard = async (buildId, buildTitle) => {
        const element = buildRefs.current[buildId];
        if (!element) return;

        setExporting(buildId);
        try {
            const canvas = await html2canvas(element, {
                backgroundColor: '#0A0A0A',
                scale: 2,
                useCORS: true,
                allowTaint: true,
                logging: false,
            });
            
            canvas.toBlob(async (blob) => {
                if (blob) {
                    try {
                        await navigator.clipboard.write([
                            new ClipboardItem({ 'image/png': blob })
                        ]);
                        toast.success(language === 'de' ? 'Build in Zwischenablage kopiert!' : 'Build copied to clipboard!');
                    } catch (err) {
                        // Fallback: Download
                        const link = document.createElement('a');
                        link.download = `meta-build-${buildTitle.replace(/\s+/g, '-').toLowerCase()}.png`;
                        link.href = canvas.toDataURL('image/png');
                        link.click();
                        toast.success(language === 'de' ? 'Build als Bild heruntergeladen!' : 'Build downloaded as image!');
                    }
                }
            }, 'image/png');
        } catch (error) {
            console.error('Copy failed:', error);
            toast.error(language === 'de' ? 'Kopieren fehlgeschlagen' : 'Copy failed');
        } finally {
            setTimeout(() => setExporting(null), 500);
        }
    };

    const filteredBuilds = builds.filter(build =>
        build.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        build.description.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
            </div>
        );
    }

    if (isPageLocked('meta')) {
        return (
            <div className="min-h-[80vh] flex items-center justify-center">
                <div className="text-center p-12 rounded-2xl bg-gradient-to-br from-red-950/50 to-red-900/30 border border-red-500/20 backdrop-blur-sm max-w-md">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center border border-red-500/30">
                        <svg className="w-10 h-10 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-3 font-['Rajdhani'] tracking-wide">{t('lockedForDev')}</h1>
                    <p className="text-gray-400 text-lg mb-2">{t('pageBeingUpdated')}</p>
                    <p className="text-gray-500 text-sm">{t('checkBackLater')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8 p-2 sm:p-6" data-testid="meta-page">
            {/* Header with Search */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-rose-500/10">
                        <Crown className="text-rose-400" size={28} />
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-['Rajdhani'] font-bold text-white">
                        Meta Builds
                    </h1>
                </div>
                <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                    <Input
                        data-testid="meta-search"
                        placeholder={language === 'de' ? 'Build suchen...' : 'Search build...'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-black/40 border-white/10"
                    />
                </div>
            </div>

            {/* Builds List */}
            {filteredBuilds.length === 0 ? (
                <div className="text-center py-16 text-gray-500">
                    <Crown size={48} className="mx-auto mb-4 opacity-50" />
                    <p>{language === 'de' ? 'Keine Meta Builds gefunden' : 'No meta builds found'}</p>
                </div>
            ) : (
                <div className="space-y-8">
                    {filteredBuilds.map((build) => {
                        const buildRelics = build.relic_ids?.map(getRelicById).filter(Boolean) || [];
                        const buildAccessories = build.accessory_ids?.map(getAccessoryById).filter(Boolean) || [];
                        const totalItems = buildRelics.length + buildAccessories.length;
                        const receivedItems = [...buildRelics, ...buildAccessories].filter(item => isReceived(item.id)).length;
                        const completionPercent = totalItems > 0 ? Math.round((receivedItems / totalItems) * 100) : 0;

                        return (
                            <div key={build.id} className="relative">
                                {/* Export Buttons */}
                                <div className="absolute top-4 right-4 z-10 flex gap-2">
                                    <Button
                                        onClick={() => copyBuildToClipboard(build.id, build.title)}
                                        disabled={exporting === build.id}
                                        size="sm"
                                        data-testid={`share-build-${build.id}`}
                                        className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-500/30"
                                    >
                                        {exporting === build.id ? (
                                            <div className="w-4 h-4 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin" />
                                        ) : (
                                            <Share2 size={16} />
                                        )}
                                        <span className="ml-2">{language === 'de' ? 'Teilen' : 'Share'}</span>
                                    </Button>
                                    <Button
                                        onClick={() => exportBuildAsImage(build.id, build.title)}
                                        disabled={exporting === build.id}
                                        size="sm"
                                        data-testid={`export-build-${build.id}`}
                                        className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/30"
                                    >
                                        {exporting === build.id ? (
                                            <div className="w-4 h-4 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
                                        ) : (
                                            <Download size={16} />
                                        )}
                                        <span className="ml-2">{language === 'de' ? 'Exportieren' : 'Export'}</span>
                                    </Button>
                                </div>

                                {/* Build Card - This is what gets exported */}
                                <div 
                                    ref={el => buildRefs.current[build.id] = el}
                                    className="bg-card rounded-xl border border-white/5 p-6 space-y-6"
                                    data-testid={`meta-build-${build.id}`}
                                >
                                    {/* Build Title with Progress */}
                                    <div className="flex items-center justify-between pr-32">
                                        <h2 className="text-2xl font-['Rajdhani'] font-bold text-white">
                                            {build.title}
                                        </h2>
                                        <div className="flex items-center gap-3">
                                            <div className="text-sm text-gray-400">
                                                <span className="text-green-400 font-bold">{receivedItems}</span>
                                                <span className="mx-1">/</span>
                                                <span>{totalItems}</span>
                                            </div>
                                            <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                                                <div 
                                                    className="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-500"
                                                    style={{ width: `${completionPercent}%` }}
                                                />
                                            </div>
                                            <span className={cn(
                                                "text-sm font-bold",
                                                completionPercent === 100 ? "text-green-400" : "text-gray-400"
                                            )}>
                                                {completionPercent}%
                                            </span>
                                        </div>
                                    </div>

                                    {/* Accessories Box */}
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <Sparkles size={18} className="text-yellow-400" />
                                            <h3 className="text-lg font-semibold text-yellow-400">Accessories</h3>
                                        </div>
                                        <div className="grid grid-cols-8 gap-3">
                                            {buildAccessories.length > 0 ? buildAccessories.map((accessory) => (
                                                <div 
                                                    key={accessory.id}
                                                    className={cn(
                                                        "rounded-lg overflow-hidden border-2 transition-all bg-black/40",
                                                        isReceived(accessory.id) 
                                                            ? "border-green-500 shadow-lg shadow-green-500/20" 
                                                            : "border-white/10"
                                                    )}
                                                >
                                                    <div className="aspect-square relative">
                                                        <img 
                                                            src={accessory.image_url || PLACEHOLDER_IMAGE} 
                                                            alt={accessory.title}
                                                            className="w-full h-full object-cover"
                                                            crossOrigin="anonymous"
                                                        />
                                                        {isReceived(accessory.id) && (
                                                            <div className="absolute top-1 right-1 bg-green-500 rounded-full p-1">
                                                                <CheckCircle size={12} className="text-white" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button
                                                        onClick={() => navigate(`/accessories/${accessory.id}`)}
                                                        className="w-full p-2 text-xs font-medium text-white hover:text-yellow-400 transition-colors truncate text-center"
                                                    >
                                                        {accessory.title}
                                                    </button>
                                                </div>
                                            )) : (
                                                <div className="col-span-8 text-center py-8 text-gray-500">
                                                    {language === 'de' ? 'Keine Accessories zugewiesen' : 'No accessories assigned'}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Relics Box */}
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <Gem size={18} className="text-cyan-400" />
                                            <h3 className="text-lg font-semibold text-cyan-400">Relics</h3>
                                        </div>
                                        <div className="grid grid-cols-8 gap-3">
                                            {buildRelics.length > 0 ? buildRelics.map((relic) => (
                                                <div 
                                                    key={relic.id}
                                                    className={cn(
                                                        "rounded-lg overflow-hidden border-2 transition-all bg-black/40",
                                                        isReceived(relic.id) 
                                                            ? "border-green-500 shadow-lg shadow-green-500/20" 
                                                            : "border-white/10"
                                                    )}
                                                >
                                                    <div className="aspect-square relative">
                                                        <img 
                                                            src={relic.image_url || PLACEHOLDER_IMAGE} 
                                                            alt={relic.title}
                                                            className="w-full h-full object-cover"
                                                            crossOrigin="anonymous"
                                                        />
                                                        {isReceived(relic.id) && (
                                                            <div className="absolute top-1 right-1 bg-green-500 rounded-full p-1">
                                                                <CheckCircle size={12} className="text-white" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button
                                                        onClick={() => navigate(`/relics/${relic.id}`)}
                                                        className="w-full p-2 text-xs font-medium text-white hover:text-cyan-400 transition-colors truncate text-center"
                                                    >
                                                        {relic.title}
                                                    </button>
                                                </div>
                                            )) : (
                                                <div className="col-span-8 text-center py-8 text-gray-500">
                                                    {language === 'de' ? 'Keine Relics zugewiesen' : 'No relics assigned'}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Talents & Description */}
                                    <div className="grid grid-cols-5 gap-6">
                                        {/* Talents (2/5) */}
                                        <div className="col-span-2 bg-black/40 rounded-lg p-4 border border-white/10">
                                            <h3 className="text-lg font-semibold text-purple-400 mb-4">Talents</h3>
                                            {build.talents && build.talents.length > 0 ? (
                                                <div className="space-y-2">
                                                    {build.talents.map((talent, idx) => (
                                                        <div 
                                                            key={idx}
                                                            className="flex items-center gap-3 p-2 bg-white/5 rounded-lg"
                                                        >
                                                            <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded font-mono text-sm">
                                                                Lv. {talent.level}
                                                            </span>
                                                            <span className="text-white">{talent.name}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-gray-500 text-center py-4">
                                                    {language === 'de' ? 'Keine Talents definiert' : 'No talents defined'}
                                                </p>
                                            )}
                                        </div>

                                        {/* Description (3/5) */}
                                        <div className="col-span-3 bg-black/40 rounded-lg p-4 border border-white/10">
                                            <h3 className="text-lg font-semibold text-gray-300 mb-4">
                                                {language === 'de' ? 'Beschreibung' : 'Description'}
                                            </h3>
                                            <div className="prose prose-invert max-w-none">
                                                <p className="text-gray-400 whitespace-pre-wrap">
                                                    {build.description || (language === 'de' ? 'Keine Beschreibung vorhanden' : 'No description available')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Watermark for exported image */}
                                    <div className="flex items-center justify-between pt-4 border-t border-white/5 text-xs text-gray-600">
                                        <span>SystemHunter Wiki</span>
                                        <span>{user?.username || 'Anonymous'} • {new Date().toLocaleDateString()}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MetaPage;
